import { Box, SxProps, Theme, styled } from '@mui/material';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import React from 'react';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import {
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    UseFormStateReturn,
    useFormContext,
} from 'react-hook-form';
import { Button } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
interface BaseFileUploadProps {
    name: string;
    sx?: SxProps;
    multiple?: boolean;
    disabled?: boolean;
    accept?: Record<string, string[]>;
    description?: string;
    uploadLabel?: string;
}
const getDropzoneColor = (props: DropzoneRootProps, theme: Theme) => {
    if (props.isDragAccept) {
        return theme.palette.success.main;
    }
    if (props.isDragReject) {
        return theme.palette.error.main;
    }
    if (props.isFocused) {
        return theme.palette.primary.main;
    }
    return colors.main;
};
const Dropzone = styled('div')(({ theme, ...props }) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '15rem',
    height: '15rem',
    borderColor: getDropzoneColor(props, theme),
    backgroundColor: common.white,
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
}));
const BaseFileUpload: React.FC<BaseFileUploadProps> = ({
    name,
    multiple,
    disabled,
    sx,
    description = 'Định dạng .xlsx và không quá 5MB',
    uploadLabel = 'Tải file',
    accept = {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
        ],
    },
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState, formState }) => (
                <FileUploadRender
                    field={field}
                    fieldState={fieldState}
                    formState={formState}
                    sx={sx}
                    multiple={multiple}
                    disabled={disabled}
                    description={description}
                    uploadLabel={uploadLabel}
                    accept={accept}
                />
            )}
        />
    );
};

const FileUploadRender = ({
    field,
    multiple,
    disabled,
    sx,
    description,
    uploadLabel,
    accept,
}: {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
    sx?: SxProps;
    multiple?: boolean;
    disabled?: boolean;
    description: string;
    uploadLabel: string;
    accept?: Record<string, string[]>;
}) => {
    const onDrop = (acceptedFiles: File[]) => {
        if (multiple) {
            field.onChange([...(field.value || []), ...acceptedFiles]);
        } else {
            field.onChange(acceptedFiles[0]);
        }
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        accept,
        onDrop,
        multiple: multiple,
        disabled: disabled,
    });

    const renderFile = (file: File | string) => {
        if (file instanceof File) {
            return <>{file.name}</>;
        }
    };
    return (
        <>
            <Dropzone
                {...getRootProps({
                    isFocused,
                    isDragAccept,
                    isDragReject,
                    sx: {
                        ...sx,
                        position: 'relative',
                        borderRadius: '8px',
                    },
                })}
            >
                <input {...getInputProps()} />
                {multiple
                    ? field?.value?.map((file: File) => renderFile(file))
                    : field?.value && renderFile(field?.value)}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Box sx={{ fontSize: '20px', color: common.black }}>
                        {description}
                    </Box>
                    <Button
                        variant="outlined"
                        component="span"
                        sx={{
                            ...styles.btnUpload,
                            visibility: disabled ? 'hidden' : 'visible',
                        }}
                        disabled={disabled}
                        startIcon={<FileUploadOutlinedIcon />}
                    >
                        {uploadLabel}
                    </Button>
                </Box>
            </Dropzone>
        </>
    );
};

const styles = createStyleSheet({
    btnUpload: {
        backgroundColor: common.white,
        textTransform: 'none',
        minWidth: '170px',
        height: '60px',
        fontSize: '20px',
        fontWeight: '700',
        borderRadius: '40px',
        '&:hover': {
            backgroundColor: common.white,
        },
    },
});

export default BaseFileUpload;
